import { useCallback, useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertCircle, History, Link2 } from 'lucide-react';
import type { ActivityApiRow, ActivityQueryParams } from '@command-center/logging-sdk';
import { Badge, Button, Card } from '@command-center/ui-system';
import { groupRepeats, isSystemRow, type ActivityGroup } from './activity-grouping';
import { LogDrawer, LogField, LogJson } from './log-drawer';
import {
  LogDegradedBanner,
  LogEmptyState,
  LogErrorState,
  LogSkeleton,
} from './log-states';
import { LogFilterBar, type FilterField } from './LogFilterBar';
import { logQueryKey, rangeToSince, useLogFilters } from './log-filters';
import {
  composeActivityParams,
  directoryField,
  logViewState,
  visibleFilters,
} from './log-view-model';
import type { ActivityLogViewerProps } from './types';

const CATEGORIES = ['auth', 'record', 'payment', 'portal', 'email', 'automation', 'ai', 'system'];
const OUTCOMES = ['success', 'waiting', 'failure', 'blocked', 'cancelled', 'partial'];
const ACTOR_TYPES = ['user', 'system', 'portal-token', 'stripe', 'ai'];

/**
 * The one Activity timeline component, for every portal.
 *
 * `scope` is enforced, not suggested: those filters are merged last and cannot
 * be cleared from the toolbar, so a User, Organization, record, or Switch view
 * is the same master log narrowed on the server. There is deliberately no
 * per-surface log store and no client-side hiding of rows the caller shouldn't
 * have fetched — this merge is convenience, and the Worker applies the same
 * rule again to whatever actually arrives.
 *
 * `stream` is part of that enforced scope. User Activity and System Activity
 * are two filtered reads of one table, not two logs.
 */
export function ActivityLogViewer({
  client,
  scope,
  storageKey,
  filterOptions,
  hiddenFilters = [],
  labels,
  renderRowActions,
  showAttention = false,
  compact = false,
}: ActivityLogViewerProps) {
  const { values, range, setValue, setRange, clear } = useLogFilters(storageKey, '30');
  const [selected, setSelected] = useState<ActivityGroup<ActivityApiRow> | null>(null);

  // `scope` is usually an inline object literal, so its identity changes on
  // every render. Serialize it for the dependencies below, otherwise the memos
  // rerun constantly.
  const scopeKey = JSON.stringify(scope ?? {});

  const fields = useMemo<FilterField[]>(() => {
    const all: FilterField[] = [
      { key: 'category', label: 'Category', options: CATEGORIES.map(opt) },
      { key: 'outcome', label: 'Status', options: OUTCOMES.map(opt) },
      { key: 'actorType', label: 'Actor', options: ACTOR_TYPES.map(opt) },
      directoryField('clientId', 'Organization', filterOptions?.organizations),
      directoryField('appId', 'App', filterOptions?.apps),
      { key: 'entityType', label: 'Record', placeholder: 'Record type' },
      { key: 'switchKey', label: 'Switch', placeholder: 'Switch key' },
    ].filter((f): f is FilterField => f !== null);
    return visibleFilters(all, hiddenFilters, JSON.parse(scopeKey) as object);
  }, [filterOptions, hiddenFilters, scopeKey]);

  const params = useMemo<ActivityQueryParams>(
    () => composeActivityParams(values, JSON.parse(scopeKey) as never, compact ? 25 : 50),
    [values, scopeKey, compact],
  );

  const query = useInfiniteQuery({
    // `range` is part of the key, but the timestamp it resolves to is NOT:
    // rangeToSince() reads the clock, so putting it in the key would produce a
    // different key on every render and refetch forever.
    queryKey: logQueryKey('activity', params, range),
    queryFn: ({ pageParam }) =>
      client.list({ ...params, since: rangeToSince(range), cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: 60_000,
  });

  const pages = query.data?.pages ?? [];
  const rows = pages.flatMap((p) => p.items);
  const groups = useMemo(() => groupRepeats(rows), [rows]);
  const degraded = pages[0]?.degraded;
  const note = pages[0]?.note;

  const handleChange = useCallback(
    (key: string, value: string | undefined) => setValue(key, value),
    [setValue],
  );

  const state = logViewState({
    isError: query.isError,
    isLoading: query.isLoading,
    itemCount: groups.length,
  });

  return (
    <div className="space-y-3">
      <LogFilterBar
        fields={fields}
        values={values}
        onChange={handleChange}
        onClear={clear}
        range={range}
        onRangeChange={setRange}
        searchPlaceholder={labels?.searchPlaceholder ?? 'Search summary, action, or record…'}
      />

      {showAttention && client.attention && <AttentionBanner client={client} />}

      {degraded && <LogDegradedBanner note={note ?? 'The activity log store is unavailable.'} />}

      {state === 'error' ? (
        <LogErrorState title="Couldn't load activity" error={query.error} />
      ) : state === 'loading' ? (
        <LogSkeleton />
      ) : (
        <Card className="overflow-hidden">
          {state === 'empty' ? (
            <LogEmptyState
              icon={History}
              message={labels?.empty ?? 'No activity logged yet.'}
            />
          ) : (
            <>
              <ul className="divide-y divide-border">
                {groups.map((group) => (
                  <li key={group.key}>
                    <TimelineRow group={group} onOpen={() => setSelected(group)} />
                  </li>
                ))}
              </ul>

              {query.hasNextPage && (
                <div className="border-t border-border p-2 text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => query.fetchNextPage()}
                    disabled={query.isFetchingNextPage}
                  >
                    {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
                  </Button>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {selected && (
        <ActivityDetail
          group={selected}
          onClose={() => setSelected(null)}
          renderRowActions={renderRowActions}
        />
      )}
    </div>
  );
}

/**
 * Unresolved background work, shown above System Activity.
 *
 * User Activity is the default view, which is only defensible if a failure
 * nobody is watching still surfaces. Waiting jobs are excluded on purpose —
 * an expected dependency wait is not something to act on.
 */
function AttentionBanner({ client }: { client: ActivityLogViewerProps['client'] }) {
  const { data } = useQuery({
    queryKey: ['activity', 'attention'],
    queryFn: () => client.attention!(),
    refetchInterval: 120_000,
  });
  if (!data) return null;
  if (!data.available) {
    return (
      <Card className="p-3 text-[12px] text-muted-foreground">
        The background work queue could not be read, so unresolved failures are unknown.
      </Card>
    );
  }
  if (!data.count) {
    return (
      <Card className="p-3 text-[12px] text-muted-foreground">
        No unresolved background failures.
        {data.mirror.waiting > 0
          && ` ${data.mirror.waiting} mirror job(s) are waiting on a dependency.`}
      </Card>
    );
  }
  const oldest = data.oldestUnresolved;
  return (
    <Card className="flex items-start gap-2 border-destructive/40 bg-destructive/5 p-3 text-[12px]">
      <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="font-medium">
          {data.count} unresolved background job{data.count === 1 ? '' : 's'}
          {data.mirror.blocked > 0 && ` · ${data.mirror.blocked} blocked`}
          {data.mirror.failed > 0 && ` · ${data.mirror.failed} retrying`}
        </div>
        {oldest && (
          <div className="mt-0.5 text-muted-foreground">
            Oldest:{' '}
            <Link to={oldest.href} className="text-primary hover:underline">
              {oldest.subjectName}
            </Link>{' '}
            — {oldest.status.toLowerCase()} since {new Date(oldest.since).toLocaleString()}.
            {oldest.reason ? ` ${oldest.reason}` : ''}
          </div>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

function TimelineRow({
  group,
  onOpen,
}: {
  group: ActivityGroup<ActivityApiRow>;
  onOpen: () => void;
}) {
  const { row } = group;
  const system = isSystemRow(row);
  const repeats = group.rows.length;
  // Routine successes stay legible but recede, so failures are what the eye
  // lands on. They are never hidden.
  const quiet = row.outcome === 'success';

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-start gap-3 px-4 py-2.5 text-left hover:bg-muted/40"
    >
      <OutcomeDot outcome={row.outcome} />
      <div className="min-w-0 flex-1">
        <div className={`text-[13px] ${quiet ? 'text-muted-foreground' : ''}`}>
          {system ? (
            // A system row's summary is already a whole sentence naming the
            // subject. Prefixing it with the actor and the raw action key is
            // what produced "Airtable mirror airtable mirror failed".
            <span className={quiet ? '' : 'font-medium'}>
              {row.summary || formatLabel(row.action)}
            </span>
          ) : (
            <>
              <span className="font-medium">{row.actorName}</span>{' '}
              <span className="text-muted-foreground">{formatLabel(row.action)}</span>{' '}
              {row.entityLabel && <span className="font-medium">{row.entityLabel}</span>}
            </>
          )}
          {repeats > 1 && (
            <span className="text-muted-foreground"> · {repeats} repeated events</span>
          )}
        </div>

        {detailLine(row) && (
          <div className="mt-0.5 truncate text-[12px] text-muted-foreground">
            {detailLine(row)}
          </div>
        )}

        {repeats > 1 && (
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            first seen {new Date(group.firstSeen).toLocaleString()} · last seen{' '}
            {new Date(group.lastSeen).toLocaleString()}
          </div>
        )}

        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <time className="text-[11px] text-muted-foreground sm:hidden">
            {new Date(row.createdAt).toLocaleString()}
          </time>
          {row.appId && <Badge variant="muted" className="text-[10px]">{row.appId}</Badge>}
          {row.clientId && <Badge variant="muted" className="text-[10px]">{row.clientId}</Badge>}
          {row.category && <Badge variant="muted" className="text-[10px]">{row.category}</Badge>}
          {row.switchKey && (
            <Badge variant="info" className="text-[10px]">switch: {row.switchKey}</Badge>
          )}
        </div>
      </div>
      <time className="hidden shrink-0 whitespace-nowrap pt-0.5 text-[11px] text-muted-foreground sm:block">
        {new Date(row.createdAt).toLocaleString()}
      </time>
    </button>
  );
}

/**
 * The second line. For a system row that is the explanation the writer
 * supplied; for a user row it is the summary, unless the summary is just the
 * action key repeated.
 */
function detailLine(row: ActivityApiRow): string | null {
  if (isSystemRow(row)) return row.notes && row.notes !== row.summary ? row.notes : null;
  return row.summary && row.summary !== row.action ? row.summary : null;
}

// ---------------------------------------------------------------------------
// Detail drawer
// ---------------------------------------------------------------------------

function ActivityDetail({
  group,
  onClose,
  renderRowActions,
}: {
  group: ActivityGroup<ActivityApiRow>;
  onClose: () => void;
  renderRowActions?: (row: ActivityApiRow) => React.ReactNode;
}) {
  const row = group.row;

  return (
    <LogDrawer
      title={row.summary || formatLabel(row.action)}
      description={
        <>
          <Badge variant="muted">{new Date(row.createdAt).toLocaleString()}</Badge>
          {row.outcome && <Badge variant={outcomeVariant(row.outcome)}>{row.outcome}</Badge>}
          {row.category && <Badge variant="muted">{row.category}</Badge>}
          {group.rows.length > 1 && (
            <Badge variant="warning">{group.rows.length} repeated events</Badge>
          )}
        </>
      }
      icon={History}
      onClose={onClose}
    >
      <div className="mt-4 space-y-4 overflow-y-auto pb-8 text-[13px]">
        {row.notes && <LogField label="What happened">{row.notes}</LogField>}

        {renderRowActions?.(row)}

        <LogField label="Actor">
          {row.actorName}
          {row.actorEmail && <span className="text-muted-foreground"> · {row.actorEmail}</span>}
          {row.actorType && <span className="text-muted-foreground"> · {row.actorType}</span>}
        </LogField>
        <LogField label="App / Organization">
          {row.appId ?? '—'} / {row.clientId ?? '—'}
          {row.portal && <span className="text-muted-foreground"> · {row.portal}</span>}
        </LogField>
        <LogField label="Record">
          {row.entityType}
          {row.entityLabel ? ` · ${row.entityLabel}` : ''}
          {row.entityHref && (
            <Link
              to={row.entityHref}
              className="ml-2 inline-flex items-center gap-1 text-primary hover:underline"
            >
              <Link2 className="h-3 w-3" aria-hidden /> open
            </Link>
          )}
        </LogField>
        {/* The raw action key lives here and nowhere else — it is useful when
            filtering or reading the database, and unreadable in a timeline. */}
        <LogField label="Action key">
          <code className="rounded bg-muted/60 px-1 py-0.5 text-[12px]">{row.action}</code>
        </LogField>
        {(row.switchKey || row.switchExecutionId || row.correlationId) && (
          <LogField label="Run">
            {row.switchKey && <div>Switch: {row.switchKey}</div>}
            {row.switchExecutionId && <div>Execution: {row.switchExecutionId}</div>}
            {row.correlationId && <div>Correlation: {row.correlationId}</div>}
          </LogField>
        )}
        {row.source && <LogField label="Source">{row.source}</LogField>}
        {!row.outcome && (
          <p className="text-[12px] text-muted-foreground">
            No outcome was recorded for this event. That is not the same as a success — events
            written before outcome tracking existed carry no value.
          </p>
        )}

        {group.rows.length > 1 && (
          <LogField label={`Raw events (${group.rows.length})`}>
            <ul className="mt-1 max-h-56 space-y-0.5 overflow-auto rounded-md bg-muted/50 p-2 text-[11px]">
              {group.rows.map((raw) => (
                <li key={raw.id} className="flex justify-between gap-3">
                  <span className="truncate text-muted-foreground">{raw.id}</span>
                  <span className="whitespace-nowrap">
                    {new Date(raw.createdAt).toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Every repeat is still stored. Grouping is a display choice, not a deletion.
            </p>
          </LogField>
        )}

        {row.oldValue != null && <LogJson label="Before" value={row.oldValue} />}
        {row.newValue != null && <LogJson label="After" value={row.newValue} />}
        {row.metadata && Object.keys(row.metadata).length > 0 && (
          <LogJson label="Metadata" value={row.metadata} />
        )}
      </div>
    </LogDrawer>
  );
}

function OutcomeDot({ outcome }: { outcome?: string }) {
  const color =
    outcome === 'failure'
      ? 'bg-destructive'
      : outcome === 'blocked' || outcome === 'partial'
        ? 'bg-warning'
        : outcome === 'waiting'
          ? 'bg-info'
          : outcome === 'success'
            ? 'bg-success'
            : 'bg-muted-foreground/30';
  return (
    <span
      className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${color}`}
      title={outcome ?? 'outcome not recorded'}
      aria-hidden
    />
  );
}

function outcomeVariant(
  outcome: string,
): 'success' | 'warning' | 'destructive' | 'info' | 'muted' {
  if (outcome === 'success') return 'success';
  if (outcome === 'failure') return 'destructive';
  if (outcome === 'blocked' || outcome === 'partial') return 'warning';
  if (outcome === 'waiting') return 'info';
  return 'muted';
}

function opt(value: string) {
  return { value, label: value };
}

function formatLabel(value: string): string {
  return value.replaceAll('_', ' ');
}
